// Jungle Ruins — stage decals / carved reliefs for the mural atlas (see src/world/murals.js).
//
// drawMurals(g, R, kit) draws into the canvas 2D context `g` inside R = { x, y, w, h } (2048 x 1008)
// and returns the table entries for mural ids 4..8:
//   4 cloisterRoof   Overgrown Cloister Gallery roof (10.0 x 11.0 m): Mesoamerican step-fret relief & solar emblem
//   5 cloisterWall   Overgrown Cloister Gallery east wall (11.0 x 1.3 m): carved feathered-serpent & glyph frieze
//   6 sanctuaryDeck  Upper Temple Sanctuary spawn terrace (18.0 x 7.2 m): jade & gold temple border & altar seal
//   7 sunAltar       Central Sun Calendar Altar top (5.6 x 5.6 m): radial golden Sun Calendar wheel & glyphs
//   8 shrineFrieze   Central Shrine Pavilion wall (4.4 x 2.7 m): carved temple guardian mask & expedition stencil
export const MURAL_IDS = {
  cloisterRoof: 4,
  cloisterWall: 5,
  sanctuaryDeck: 6,
  sunAltar: 7,
  shrineFrieze: 8,
};
export const MURAL = MURAL_IDS;

const GOLD = 'rgba(232, 184, 62, 0.94)';
const GOLD_DK = 'rgba(168, 122, 34, 0.92)';
const JADE = 'rgba(46, 138, 98, 0.90)';
const JADE_LT = 'rgba(92, 188, 138, 0.90)';
const STONE_LT = 'rgba(232, 224, 202, 0.92)';
const BASALT = 'rgba(38, 42, 36, 0.92)';
const CINNABAR = 'rgba(184, 64, 46, 0.90)';

function stencilBorder(g, x, y, w, h, inset, lw, color) {
  g.save();
  g.strokeStyle = color;
  g.lineWidth = lw;
  g.strokeRect(x + inset, y + inset, w - inset * 2, h - inset * 2);
  g.restore();
}

function drawSunRays(g, cx, cy, rOuter, rInner, pts, c1, c2) {
  for (let i = 0; i < pts; i++) {
    const a0 = (i / pts) * Math.PI * 2 - Math.PI / 2;
    const aL = a0 - Math.PI / pts;
    const aR = a0 + Math.PI / pts;
    const rTip = i % 2 === 0 ? rOuter : rOuter * 0.76;
    g.fillStyle = c1;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(cx + Math.cos(a0) * rTip, cy + Math.sin(a0) * rTip);
    g.lineTo(cx + Math.cos(aL) * rInner, cy + Math.sin(aL) * rInner);
    g.closePath();
    g.fill();
    g.fillStyle = c2;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(cx + Math.cos(a0) * rTip, cy + Math.sin(a0) * rTip);
    g.lineTo(cx + Math.cos(aR) * rInner, cy + Math.sin(aR) * rInner);
    g.closePath();
    g.fill();
  }
}

function drawStepFretBand(g, x, y, w, h, count, color) {
  g.save();
  g.strokeStyle = color;
  g.lineWidth = 4;
  const sw = w / count;
  for (let i = 0; i < count; i++) {
    const bx = x + i * sw;
    g.beginPath();
    g.moveTo(bx + 4, y + h - 4);
    g.lineTo(bx + 4, y + h * 0.55);
    g.lineTo(bx + sw * 0.35, y + h * 0.55);
    g.lineTo(bx + sw * 0.35, y + 4);
    g.lineTo(bx + sw - 4, y + 4);
    g.lineTo(bx + sw - 4, y + h * 0.72);
    g.lineTo(bx + sw * 0.62, y + h * 0.72);
    g.lineTo(bx + sw * 0.62, y + h * 0.36);
    g.stroke();
  }
  g.restore();
}

export function drawMurals(g, R, kit) {
  const out = [];

  // ---------------------------------------------------------------- 4: Cloister Gallery roof top (10.0 x 11.0 m)
  {
    const r = { x: R.x + 8, y: R.y + 8, w: 560, h: 586 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
    stencilBorder(g, r.x, r.y, r.w, r.h, 24, 8, GOLD);
    stencilBorder(g, r.x, r.y, r.w, r.h, 38, 4, JADE);
    drawStepFretBand(g, r.x + 48, r.y + 48, r.w - 96, 36, 8, GOLD);
    drawStepFretBand(g, r.x + 48, r.y + r.h - 84, r.w - 96, 36, 8, GOLD);

    // Header relief plaque
    g.fillStyle = BASALT;
    g.fillRect(r.x + 56, r.y + 98, r.w - 112, 82);
    stencilBorder(g, r.x + 56, r.y + 98, r.w - 112, 82, 6, 3, GOLD);
    g.fillStyle = GOLD;
    g.font = kit.font(42);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('TEMPLO DEL SOL', r.x + r.w / 2, r.y + 139);

    // Central solar sanctuary seal
    const cx = r.x + r.w / 2, cy = r.y + 305;
    g.strokeStyle = BASALT;
    g.lineWidth = 8;
    g.beginPath(); g.arc(cx, cy, 92, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = JADE;
    g.lineWidth = 5;
    g.beginPath(); g.arc(cx, cy, 78, 0, Math.PI * 2); g.stroke();
    drawSunRays(g, cx, cy, 70, 22, 12, GOLD, CINNABAR);
    g.fillStyle = BASALT;
    g.beginPath(); g.arc(cx, cy, 26, 0, Math.PI * 2); g.fill();
    g.fillStyle = JADE_LT;
    g.beginPath(); g.arc(cx, cy, 14, 0, Math.PI * 2); g.fill();

    // Lower ritual court markers
    const bayY = r.y + 422;
    for (let i = 0; i < 3; i++) {
      const bx = r.x + 62 + i * 150;
      g.strokeStyle = STONE_LT;
      g.lineWidth = 4;
      g.strokeRect(bx, bayY, 136, 68);
      g.fillStyle = GOLD;
      g.font = kit.fontB(20);
      g.fillText(`ALTAR ${i + 1}`, bx + 68, bayY + 34);
    }
    g.restore();
    out.push({ id: MURAL_IDS.cloisterRoof, ...r, m: [10.0, 11.0], fx: [0.9, 1] });
  }

  // ---------------------------------------------------------------- 5: Cloister Gallery east wall frieze (11.0 x 1.3 m)
  {
    const r = { x: R.x + 584, y: R.y + 8, w: 880, h: 160 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
    g.fillStyle = BASALT;
    g.fillRect(r.x + 20, r.y + 16, r.w - 40, r.h - 32);
    stencilBorder(g, r.x + 20, r.y + 16, r.w - 40, r.h - 32, 6, 5, GOLD);
    stencilBorder(g, r.x + 20, r.y + 16, r.w - 40, r.h - 32, 14, 3, JADE);
    drawStepFretBand(g, r.x + 42, r.y + 34, 210, 92, 3, JADE_LT);
    drawStepFretBand(g, r.x + r.w - 252, r.y + 34, 210, 92, 3, JADE_LT);

    g.fillStyle = GOLD;
    g.font = kit.font(40);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('CLOISTER OF THE SERPENT', r.x + r.w / 2, r.y + 64);

    g.fillStyle = STONE_LT;
    g.font = kit.fontB(20);
    g.fillText('ARCHAEOLOGICAL RESERVE  ·  CENOTE BASIN  ·  1928', r.x + r.w / 2, r.y + 110);
    g.restore();
    out.push({ id: MURAL_IDS.cloisterWall, ...r, m: [11.0, 1.3], fx: [0.88, 1] });
  }

  // ---------------------------------------------------------------- 6: Upper Temple Sanctuary spawn terrace (18 x 7.2 m)
  {
    const r = { x: R.x + 584, y: R.y + 232, w: 900, h: 360 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
    stencilBorder(g, r.x, r.y, r.w, r.h, 20, 7, GOLD);
    stencilBorder(g, r.x, r.y, r.w, r.h, 34, 4, JADE);
    drawStepFretBand(g, r.x + 46, r.y + 44, r.w - 92, 28, 14, GOLD_DK);
    drawStepFretBand(g, r.x + 46, r.y + r.h - 72, r.w - 92, 28, 14, GOLD_DK);

    // Central carved sanctuary threshold tablet
    const mx = r.x + r.w / 2 - 220, my = r.y + r.h / 2 - 66, mw = 440, mh = 132;
    g.fillStyle = BASALT;
    g.fillRect(mx, my, mw, mh);
    stencilBorder(g, mx, my, mw, mh, 8, 5, GOLD);
    stencilBorder(g, mx, my, mw, mh, 16, 2, JADE_LT);
    g.fillStyle = GOLD;
    g.font = kit.font(34);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('SANCTUARY OF THE SUN', r.x + r.w / 2, r.y + r.h / 2 - 16);
    g.fillStyle = STONE_LT;
    g.font = kit.fontB(20);
    g.fillText('UPPER ZIGGURAT TERRACE  ·  SACRED GROUND', r.x + r.w / 2, r.y + r.h / 2 + 26);
    g.restore();
    out.push({ id: MURAL_IDS.sanctuaryDeck, ...r, m: [18, 7.2], fx: [0.9, 0.85] });
  }

  // ---------------------------------------------------------------- 7: Central Sun Calendar Altar (5.6 x 5.6 m)
  {
    const r = { x: R.x + 1500, y: R.y + 8, w: 520, h: 520 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
    const cx = r.x + r.w / 2, cy = r.y + r.h / 2;

    // Concentric Aztec/Mayan calendar stone rings
    g.fillStyle = BASALT;
    g.beginPath(); g.arc(cx, cy, 232, 0, Math.PI * 2); g.fill();
    g.strokeStyle = GOLD;
    g.lineWidth = 12;
    g.beginPath(); g.arc(cx, cy, 224, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = JADE_LT;
    g.lineWidth = 7;
    g.beginPath(); g.arc(cx, cy, 202, 0, Math.PI * 2); g.stroke();

    // 20 calendar day-glyph radial ticks around outer ring
    g.strokeStyle = GOLD;
    g.lineWidth = 5;
    for (let i = 0; i < 20; i++) {
      const a = (i / 20) * Math.PI * 2;
      g.beginPath();
      g.moveTo(cx + Math.cos(a) * 176, cy + Math.sin(a) * 176);
      g.lineTo(cx + Math.cos(a) * 198, cy + Math.sin(a) * 198);
      g.stroke();
    }

    drawSunRays(g, cx, cy, 174, 48, 16, GOLD, CINNABAR);
    drawSunRays(g, cx, cy, 114, 34, 8, JADE, GOLD);

    g.fillStyle = BASALT;
    g.beginPath(); g.arc(cx, cy, 56, 0, Math.PI * 2); g.fill();
    g.strokeStyle = GOLD;
    g.lineWidth = 5;
    g.beginPath(); g.arc(cx, cy, 48, 0, Math.PI * 2); g.stroke();
    g.fillStyle = JADE_LT;
    g.beginPath(); g.arc(cx, cy, 22, 0, Math.PI * 2); g.fill();
    g.restore();
    out.push({ id: MURAL_IDS.sunAltar, ...r, m: [5.6, 5.6], fx: [0.92, 0.85] });
  }

  // ---------------------------------------------------------------- 8: Central Shrine Pavilion Frieze (4.4 x 2.7 m)
  {
    const r = { x: R.x + 8, y: R.y + 612, w: 540, h: 330 };
    g.save();
    g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();

    // Left: Carved Golden Temple Mask Emblem
    const mx = r.x + 145, my = r.y + 165;
    g.fillStyle = BASALT;
    g.fillRect(mx - 105, my - 108, 210, 216);
    stencilBorder(g, mx - 105, my - 108, 210, 216, 8, 5, GOLD);
    drawSunRays(g, mx, my, 82, 28, 8, GOLD, JADE);
    g.fillStyle = BASALT;
    g.fillRect(mx - 38, my - 36, 76, 72);
    g.strokeStyle = GOLD;
    g.lineWidth = 4;
    g.strokeRect(mx - 38, my - 36, 76, 72);
    // Jade eyes & mouth bar
    g.fillStyle = JADE_LT;
    g.fillRect(mx - 26, my - 20, 18, 14);
    g.fillRect(mx + 8, my - 20, 18, 14);
    g.fillStyle = CINNABAR;
    g.fillRect(mx - 22, my + 8, 44, 14);

    // Right: Archaeological Expedition Camp Stencil Board
    const bx = r.x + 272, by = r.y + 54, bw = 244, bh = 222;
    g.fillStyle = STONE_LT;
    g.fillRect(bx, by, bw, bh);
    stencilBorder(g, bx, by, bw, bh, 8, 5, BASALT);
    g.fillStyle = JADE;
    g.fillRect(bx + 16, by + 16, bw - 32, 44);
    g.fillStyle = STONE_LT;
    g.font = kit.font(24);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('EXPEDITION SITE', bx + bw / 2, by + 38);

    g.fillStyle = BASALT;
    g.font = kit.fontB(20);
    g.fillText('SUN ALTAR RUINS', bx + bw / 2, by + 92);
    g.fillText('EXCAVATION ZONE', bx + bw / 2, by + 120);
    g.fillStyle = CINNABAR;
    g.font = kit.font(24);
    g.fillText('CENOTE COURT', bx + bw / 2, by + 160);
    g.fillStyle = BASALT;
    g.font = kit.fontB(18);
    g.fillText('SECTOR I  ·  RELIC VAULT', bx + bw / 2, by + 196);
    g.restore();
    out.push({ id: MURAL_IDS.shrineFrieze, ...r, m: [4.4, 2.7], fx: [0.88, 1] });
  }

  return out;
}
